import React, { useMemo, useRef } from 'react'
import { useTooltips } from '../../../providers/TooltipProvider'
import useSeatmapStore from '../../../../../../../../../stores/seatmapStore'

const TOOLTIP_TIMEOUT = 500
/** ~0.95 when pitch ≈ seat.w (dense circular grid). */
const SEAT_SCALE = 0.95

const Seat = React.memo(({ seat }) => {
  const selectSpecificSeat = useSeatmapStore(
    (state) => state.selectSpecificSeat
  )
  const unselectSeat = useSeatmapStore((state) => state.unselectSeat)
  const seatLookupTable = useSeatmapStore((state) => state.seatLookupTable)
  const seatOffersLookupTable = useSeatmapStore(
    (state) => state.seatOffersLookupTable
  )
  const seatBorderRadius = useSeatmapStore((state) => state.seatBorderRadius)

  const ticketGroup = useMemo(
    () => seatLookupTable[seat.seatId],
    [seatLookupTable, seat.seatId]
  )

  const seatOffers = useMemo(
    () => seatOffersLookupTable[seat.seatId] || [],
    [seatOffersLookupTable, seat.seatId]
  )

  const hasMultipleOffers = seatOffers.length > 1

  const { showTooltip, hideTooltip, isTooltipActiveForSeat } = useTooltips()
  const hoverTimerRef = useRef(null)
  const hoverCoordsRef = useRef({ x: 0, y: 0 })
  const tooltipMoveRafRef = useRef(null)

  const handleSeatClick = (event) => {
    clearTimeout(hoverTimerRef.current)
    if (
      ticketGroup?.offer?.accessCode &&
      ticketGroup?.offer?.accessCode !== null &&
      !ticketGroup?.offer?.unlocked
    ) {
      showTooltip(seat.seatId, event.clientX, event.clientY)
    } else if (window.innerWidth <= 768 || hasMultipleOffers) {
      showTooltip(seat.seatId, event.clientX, event.clientY)
    } else {
      selectSpecificSeat(seat.seatId, ticketGroup)
    }
  }

  const handleUnselectSeat = () => {
    clearTimeout(hoverTimerRef.current)
    unselectSeat(seat.seatId, ticketGroup)
  }

  const handleMouseMove = (event) => {
    hoverCoordsRef.current = {
      x: event.clientX,
      y: event.clientY,
    }
    if (
      !ticketGroup ||
      window.innerWidth <= 768 ||
      !isTooltipActiveForSeat(seat.seatId)
    ) {
      return
    }
    if (tooltipMoveRafRef.current != null) return
    tooltipMoveRafRef.current = requestAnimationFrame(() => {
      tooltipMoveRafRef.current = null
      const { x, y } = hoverCoordsRef.current
      showTooltip(seat.seatId, x, y)
    })
  }

  const handleMouseEnter = (event) => {
    if (
      window.innerWidth <= 768 ||
      hasMultipleOffers ||
      (ticketGroup?.offer?.accessCode &&
        ticketGroup?.offer?.accessCode !== null &&
        !ticketGroup?.offer?.unlocked)
    ) {
      return
    }

    hoverCoordsRef.current = {
      x: event.clientX,
      y: event.clientY,
    }
    clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = setTimeout(() => {
      const { x, y } = hoverCoordsRef.current
      showTooltip(seat.seatId, x, y)
    }, TOOLTIP_TIMEOUT)
  }

  const handleMouseLeave = () => {
    if (window.innerWidth <= 768 || hasMultipleOffers) {
      return
    }
    clearTimeout(hoverTimerRef.current)
    if (tooltipMoveRafRef.current != null) {
      cancelAnimationFrame(tooltipMoveRafRef.current)
      tooltipMoveRafRef.current = null
    }
    hideTooltip()
  }

  const getAccessibleColor = (accessibleType) => {
    if (accessibleType === 'DA') return '#2DEDB4'
    if (accessibleType === 'DB') return '#F4BC16'
    return '#F4BC16'
  }

  const accessibleType =
    seat?.accessibleType ||
    seat?.accessiblityType ||
    seat?.accessibilityType ||
    ticketGroup?.accessibleType ||
    ticketGroup?.accessiblityType ||
    ticketGroup?.accessibilityType

  const isAccessible =
    Boolean(accessibleType) ||
    Boolean(seat?.accessible || ticketGroup?.accessible)

  const seatColor = useMemo(() => {
    if (seat.selected) return '#45B26B'
    if (!ticketGroup) return '#E6E8EC'
    if (isAccessible) {
      return getAccessibleColor(accessibleType)
    }
    if (
      ticketGroup?.offer &&
      ticketGroup?.offer.accessCode &&
      !ticketGroup?.offer?.unlocked
    )
      return '#353945'
    return ticketGroup.resale
      ? '#E06C35'
      : ticketGroup?.offer?.inventoryType === 'exclusive'
        ? '#9757D7'
        : '#3E8BF7'
  }, [accessibleType, isAccessible, seat.selected, ticketGroup])

  const seatProps = {
    id: seat.seatId,
    className: `seat ${ticketGroup ? '--hover' : ''}`,
    x: seat.cx - (seat.w * SEAT_SCALE - seat.w) / 2,
    y: seat.cy - (seat.h * SEAT_SCALE - seat.h) / 2,
    rx:
      typeof seatBorderRadius === 'number'
        ? seatBorderRadius * SEAT_SCALE
        : seatBorderRadius,
    ry:
      typeof seatBorderRadius === 'number'
        ? seatBorderRadius * SEAT_SCALE
        : seatBorderRadius,
    width: seat.w * SEAT_SCALE,
    height: seat.h * SEAT_SCALE,
    fill: seatColor,
    style: { cursor: ticketGroup ? 'pointer' : 'default' },
    ...(ticketGroup && {
      onClick: seat.selected ? handleUnselectSeat : handleSeatClick,
      onMouseEnter: handleMouseEnter,
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave,
    }),
  }

  const getIconId = () => {
    if (seat.selected) return 'icon-selected'
    if (ticketGroup && ticketGroup.resale) return 'icon-resale'
    if (isAccessible) return 'icon-accessible'
    if (ticketGroup && ticketGroup?.offer?.unlocked) return 'icon-unlocked'
    if (ticketGroup && ticketGroup?.offer?.accessCode) return 'icon-locked'
    if (ticketGroup && ticketGroup?.offer?.inventoryType === 'exclusive') {
      return 'icon-vip'
    }
    return null
  }

  const iconId = getIconId()
  return (
    <g data-interactive-seat="true">
      <rect {...seatProps} />
      {iconId && (
        <use
          href={`#${iconId}`}
          x={seatProps.x + seatProps.width * 0.2}
          y={seatProps.y + seatProps.height * 0.2}
          width={seatProps.width * 0.6}
          height={seatProps.height * 0.6}
          pointerEvents="none"
          fill="white"
        />
      )}
    </g>
  )
})

export default Seat
